import Phaser from "phaser";
import { combatAction } from "../api";
import { FONT, UI, loadCreatureSprites, addCreatureSprite, type CombatEvent } from "../types";
import {
  VIEWPORT, preloadUI, addBackground, addMusicBar, addTopButton,
  addActionButton, addEnterButton, addReturnButton,
  createHPBar, createWildHPBar,
  type ImageButton, type ProgressBar,
} from "../ui";

export class CombatScene extends Phaser.Scene {
  private combatId = "";
  private player: any;
  private wild: any;
  private allCreatures: any[] = [];

  private playerSprite!: Phaser.GameObjects.Image | Phaser.GameObjects.Arc;
  // @ts-expect-error asignado en create, reservado para futuro uso
  private wildSprite!: Phaser.GameObjects.Image | Phaser.GameObjects.Arc;
  private playerName!: Phaser.GameObjects.Text;
  // @ts-expect-error asignado en create, reservado para futuro uso
  private wildName!: Phaser.GameObjects.Text;
  private playerHpBar!: ProgressBar;
  private wildHpBar!: ProgressBar;
  private logText!: Phaser.GameObjects.Text;
  private actionBtns: ImageButton[] = [];
  private busy = false;

  constructor() {
    super({ key: "CombatScene" });
  }

  preload(): void {
    preloadUI(this);
  }

  init(data: any): void {
    this.combatId = data.combatId;
    this.player = data.playerCreature;
    this.wild = data.wildCreature;
    this.allCreatures = data.allCreatures ?? [];
  }

  create(): void {
    this.busy = false;
    this.actionBtns = [];

    addBackground(this);
    addMusicBar(this);

    addTopButton(this, "map", () => {});
    addTopButton(this, "inventory", () => {});
    addTopButton(this, "options", () => {});

    // Loading text mientras cargan sprites
    const loadingText = this.add.text(VIEWPORT.x + VIEWPORT.w / 2, VIEWPORT.y + VIEWPORT.h / 2, "Cargando...", {
      fontFamily: FONT, fontSize: "13px", color: UI.TEXT,
    }).setOrigin(0.5);

    loadCreatureSprites(this, [this.player, this.wild], () => {
      loadingText.destroy();
      this.buildCombat();
    });
  }

  private buildCombat(): void {
    const vx = VIEWPORT.x, vy = VIEWPORT.y, vw = VIEWPORT.w, vh = VIEWPORT.h;

    // ── Dentro del viewport: enemigo arriba-derecha, jugador abajo-izquierda ──
    // Wild name + sprite
    this.wildName = this.add.text(vx + 10, vy + 10, `${this.wild.template_name} Nv.${this.wild.level}`, {
      fontFamily: FONT, fontSize: "11px", fontStyle: "bold", color: "#fff",
      stroke: "#000", strokeThickness: 2,
    });

    this.wildSprite = addCreatureSprite(this, vx + vw - 60, vy + 55, this.wild.sprite_key, 70);

    // Player sprite + name
    this.playerSprite = addCreatureSprite(this, vx + 60, vy + vh - 55, this.player.sprite_key, 70);

    this.playerName = this.add.text(vx + vw - 10, vy + vh - 20, `${this.player.template_name} Nv.${this.player.level}`, {
      fontFamily: FONT, fontSize: "11px", fontStyle: "bold", color: "#fff",
      stroke: "#000", strokeThickness: 2,
    }).setOrigin(1, 0);

    // Log bar sobre el viewport (mini texto abajo)
    this.logText = this.add.text(vx + vw / 2, vy + vh - 14, "¿Qué vas a hacer?", {
      fontFamily: FONT, fontSize: "10px", color: "#f5c242",
      stroke: "#000", strokeThickness: 2,
      align: "center", wordWrap: { width: vw - 20 },
    }).setOrigin(0.5, 1);

    // ── Barras debajo del viewport ──
    this.playerHpBar = createHPBar(this);
    this.wildHpBar = createWildHPBar(this);  // usa xp bar pos, amarilla
    this.updateBars();

    // ── Botones ENTER / RETURN ──
    addEnterButton(this, "CAMBIAR", () => this.doSwitch());
    addReturnButton(this, "HUIR", () => this.doFlee());

    // ── Ataques como botones 1-4 ──
    this.drawAttackButtons();
  }

  private drawAttackButtons(): void {
    for (const b of this.actionBtns) b.destroy();
    this.actionBtns = [];

    const attacks = this.player.attacks ?? [];
    attacks.slice(0, 4).forEach((atk: any, i: number) => {
      const slot = (i + 1) as 1 | 2 | 3 | 4;
      this.actionBtns.push(
        addActionButton(this, slot, atk.name.slice(0, 10), () => this.doAttack(atk.id))
      );
    });

    // Si hay menos de 4 ataques, llenar el resto con placeholders deshabilitados
    for (let i = attacks.length; i < 4; i++) {
      const slot = (i + 1) as 1 | 2 | 3 | 4;
      const b = addActionButton(this, slot, "—", () => {});
      b.setEnabled(false);
      this.actionBtns.push(b);
    }
  }

  private updateBars(): void {
    this.playerHpBar.setProgress(this.player.hp, this.player.max_hp);
    this.wildHpBar.setProgress(this.wild.hp, this.wild.max_hp);
  }

  private updatePlayerVisuals(): void {
    this.playerSprite.destroy();
    const vx = VIEWPORT.x, vy = VIEWPORT.y, vh = VIEWPORT.h;
    const key = `creature_${this.player.sprite_key}`;
    if (this.player.sprite_url && !this.textures.exists(key)) {
      this.load.image(key, this.player.sprite_url);
      this.load.once("complete", () => {
        this.playerSprite = addCreatureSprite(this, vx + 60, vy + vh - 55, this.player.sprite_key, 70);
      });
      this.load.start();
    } else {
      this.playerSprite = addCreatureSprite(this, vx + 60, vy + vh - 55, this.player.sprite_key, 70);
    }
    this.playerName.setText(`${this.player.template_name} Nv.${this.player.level}`);
  }

  private async doAttack(attackId: number): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setButtonsEnabled(false);
    try {
      const res = await combatAction(this.combatId, "attack", attackId);
      await this.playEvents(res.events);
      this.player.hp = res.player_hp;
      this.wild.hp = res.wild_hp;
      this.updateBars();
      if (res.status === "won" || res.status === "lost") {
        this.time.delayedCall(1500, () => this.scene.start("MapScene"));
      } else {
        this.busy = false;
        this.setButtonsEnabled(true);
      }
    } catch (err) {
      this.logText.setText(err instanceof Error ? err.message : "Error");
      this.busy = false;
      this.setButtonsEnabled(true);
    }
  }

  private async doFlee(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.setButtonsEnabled(false);
    try {
      const res = await combatAction(this.combatId, "flee");
      await this.playEvents(res.events);
      this.time.delayedCall(800, () => this.scene.start("MapScene"));
    } catch { this.busy = false; this.setButtonsEnabled(true); }
  }

  private async doSwitch(): Promise<void> {
    if (this.busy) return;
    const alive = this.allCreatures.filter(
      (c: any) => c.id !== this.player.id && c.current_hp > 0
    );
    if (alive.length === 0) {
      this.logText.setText("Sin otras criaturas disponibles");
      return;
    }
    this.busy = true;
    this.setButtonsEnabled(false);
    try {
      const next = alive[0];
      const res = await combatAction(this.combatId, "switch", undefined, next.id);
      await this.playEvents(res.events);
      if (res.player_name) {
        this.player.template_name = res.player_name;
        this.player.sprite_key = res.player_sprite;
        this.player.sprite_url = res.player_sprite_url;
        this.player.attacks = res.player_attacks;
        this.player.id = next.id;
        this.player.max_hp = res.player_max_hp ?? next.max_hp;
        this.player.level = next.level;
      }
      this.player.hp = res.player_hp;
      this.wild.hp = res.wild_hp;
      this.updatePlayerVisuals();
      this.updateBars();
      this.drawAttackButtons();
      if (res.status === "lost") {
        this.time.delayedCall(1500, () => this.scene.start("MapScene"));
      } else {
        this.busy = false;
        this.setButtonsEnabled(true);
      }
    } catch {
      this.busy = false;
      this.setButtonsEnabled(true);
    }
  }

  private setButtonsEnabled(enabled: boolean): void {
    for (const b of this.actionBtns) b.setEnabled(enabled);
  }

  private playEvents(events: CombatEvent[]): Promise<void> {
    return new Promise((resolve) => {
      let i = 0;
      const next = () => {
        if (i >= events.length) { resolve(); return; }
        this.logText.setText(events[i].message);
        i++;
        this.time.delayedCall(650, next);
      };
      next();
    });
  }
}
